<template>
  <div class="dashboard-layout">
    <Transition name="overlay">
      <div
        v-if="mobileSidebarOpen"
        class="dashboard-layout__overlay"
        @click="closeMobileSidebar"
      />
    </Transition>

    <Sidebar
      :menu="customerMenu"
      :collapsed="sidebarCollapsed"
      :collapsible="true"
      :mobile-open="mobileSidebarOpen"
      @toggle-collapse="toggleCollapse"
      @close-mobile="closeMobileSidebar"
    />

    <div class="dashboard-layout__main">
      <DashboardHeader
        :section-title="sectionTitle"
        :page-title="pageTitle"
        :display-name="user.name"
        :role-label="user.roleLabel"
        :avatar="user.avatar"
        :notifications="notifications"
        @toggle-sidebar="handleToggleSidebar"
        @mark-all-read="markAllAsRead"
        @mark-as-read="handleMarkAsRead"
        @view-all-notifications="goToNotifications"
        @open-profile="handleOpenProfile"
        @logout="handleLogout"
      />

      <main class="dashboard-layout__content">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/common/Sidebar.vue'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import customerMenu from '@/config/CustomerMenu.js'
import api, { resolveAvatarUrl } from '@/services/axios'
import authService from '@/services/authService'
import { useNotifications } from '@/composables/useNotifications'

export default {
  name: 'CustomersLayout',

  components: { Sidebar, DashboardHeader },

  setup() {
    // ✅ Customer notifikasi
    const {
      notifications,
      markAsRead,
      markAllAsRead
    } = useNotifications({
      role: 'customer',
      endpoint: '/customer/notifications',
      pollInterval: 60000
    })

    return {
      notifications,
      markAsRead,
      markAllAsRead
    }
  },

  data() {
    return {
      customerMenu,
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      user: {
        name: 'Pengguna',
        roleLabel: 'Customer',
        avatar: ''
      }
    }
  },

  computed: {
    sectionTitle() {
      return 'Customer'
    },

    pageTitle() {
      const routeName = this.$route.name
      if (!routeName) return 'Dashboard'

      return routeName
        .replace(/^customer-/, '')
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }
  },

  watch: {
    $route() {
      this.closeMobileSidebar()
      this.loadUser()
    }
  },

  mounted() {
    this.loadUser()
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('user-updated', this.loadUser)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('user-updated', this.loadUser)
    document.body.style.overflow = ''
  },

  methods: {
    async loadUser() {
      let cachedUser = {}

      try {
        cachedUser = JSON.parse(sessionStorage.getItem('user') || '{}')
      } catch (error) {
        console.warn('Data user di sessionStorage tidak valid.')
      }

      const cachedAvatar =
        cachedUser.avatar_url ||
        cachedUser.photo_url ||
        cachedUser.profile_photo_url ||
        cachedUser.photo ||
        cachedUser.avatar ||
        ''

      this.user = {
        name:
          cachedUser.name ||
          cachedUser.full_name ||
          cachedUser.username ||
          cachedUser.email ||
          'Pengguna',
        roleLabel: this.getRoleLabel(cachedUser),
        avatar: resolveAvatarUrl(cachedAvatar)
      }

      try {
        const response = await api.get('/profile')

        const profile =
          response.data.user ||
          response.data.data?.user ||
          response.data.data ||
          response.data

        const avatarPath =
          profile.avatar_url ||
          profile.photo_url ||
          profile.profile_photo_url ||
          profile.photo ||
          profile.avatar ||
          ''

        const updatedUser = {
          ...cachedUser,
          ...profile,
          avatar: avatarPath,
          avatar_url: profile.avatar_url || avatarPath
        }

        sessionStorage.setItem('user', JSON.stringify(updatedUser))

        this.user = {
          name:
            updatedUser.name ||
            updatedUser.full_name ||
            updatedUser.username ||
            updatedUser.email ||
            'Pengguna',
          roleLabel: this.getRoleLabel(updatedUser),
          avatar: resolveAvatarUrl(avatarPath)
        }
      } catch (error) {
        console.error(
          'Gagal mengambil profil:',
          error.response?.data || error.message
        )
      }
    },

    getRoleLabel(user) {
      const role =
        user.role_name ||
        user.role?.name ||
        (typeof user.role === 'string' ? user.role : '') ||
        user.roleLabel ||
        'Customer'

      return role.charAt(0).toUpperCase() + role.slice(1)
    },

    handleResize() {
      const isMobile = window.innerWidth <= 768
      if (!isMobile) {
        this.mobileSidebarOpen = false
        document.body.style.overflow = ''
      }
    },

    handleToggleSidebar() {
      const isMobile = window.innerWidth <= 768

      if (isMobile) {
        this.mobileSidebarOpen = !this.mobileSidebarOpen
        document.body.style.overflow = this.mobileSidebarOpen
          ? 'hidden'
          : ''
      } else {
        this.toggleCollapse()
      }
    },

    toggleCollapse() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },

    closeMobileSidebar() {
      this.mobileSidebarOpen = false
      document.body.style.overflow = ''
    },

    /**
     * ✅ BARU: handle mark-as-read
     * Khusus welcome → set flag permanen di backend
     */
    handleMarkAsRead(id) {
      this.markAsRead(id)
    },

    goToNotifications() {
    this.$router.push({ name: 'customer-notifications' })
    },

    handleOpenProfile() {
      this.$router.push({ name: 'customer-profile' })
    },

    async handleLogout() {
      try {
        await authService.logout()
      } catch (error) {
        console.warn('Logout API gagal:', error)
      } finally {
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('user')
        this.$router.push('/login')
      }
    }
  }
}
</script>

<style scoped lang="scss">

.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background-color: var(--color-background);
  color: var(--color-text);
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.dashboard-layout__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background-color: var(--color-background);
}

.dashboard-layout__content {
  flex: 1;
  min-width: 0;
  padding: 24px;
  background-color: var(--color-background);
  color: var(--color-text);
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.dashboard-layout__overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
  background: rgba(15, 23, 42, 0.45);
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.2s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .dashboard-layout__content {
    padding: 16px;
  }
}

</style>